import type { LocalProfile } from '../persistence/ProfileRepository'

type AudioContextConstructor = typeof AudioContext

export type SoundEffect = 'jump' | 'land' | 'near-miss' | 'death' | 'milestone'

export class AudioManager {
  private context: AudioContext | null = null
  private master: GainNode | null = null
  private musicGain: GainNode | null = null
  private sfxGain: GainNode | null = null
  private windGain: GainNode | null = null
  private musicOscillators: OscillatorNode[] = []
  private effectOscillator: OscillatorNode | null = null
  private stepTimer: number | null = null
  private sequenceStep = 0
  private muted = false
  private musicVolume = 0.55
  private sfxVolume = 0.8

  public async unlock(): Promise<void> {
    if (this.context === null) this.initialize()
    await this.context?.resume()
    this.startSequencer()
    this.applyLevels()
  }

  public applyProfile(profile: LocalProfile): void {
    this.muted = profile.muted
    this.musicVolume = profile.musicVolume
    this.sfxVolume = profile.sfxVolume
    this.applyLevels()
  }

  public setSpeed(normalizedSpeed: number): void {
    if (this.windGain === null || this.context === null) return
    const level = this.muted ? 0 : Math.min(0.16, Math.max(0, normalizedSpeed) * 0.12)
    this.windGain.gain.setTargetAtTime(level, this.context.currentTime, 0.08)
  }

  public effect(effect: SoundEffect): void {
    if (this.context === null || this.effectOscillator === null || this.sfxGain === null || this.muted) return
    const frequencies: Record<SoundEffect, number> = {
      jump: 360,
      land: 110,
      'near-miss': 740,
      death: 48,
      milestone: 920,
    }
    const now = this.context.currentTime
    this.effectOscillator.frequency.cancelScheduledValues(now)
    this.effectOscillator.frequency.setValueAtTime(frequencies[effect], now)
    this.effectOscillator.frequency.exponentialRampToValueAtTime(Math.max(28, frequencies[effect] * 0.42), now + 0.18)
    this.sfxGain.gain.cancelScheduledValues(now)
    this.sfxGain.gain.setValueAtTime(Math.max(0.0001, this.sfxVolume * 0.24), now)
    this.sfxGain.gain.exponentialRampToValueAtTime(0.0001, now + (effect === 'death' ? 0.45 : 0.16))
  }

  public suspend(): void {
    void this.context?.suspend()
  }

  public dispose(): void {
    if (this.stepTimer !== null) window.clearInterval(this.stepTimer)
    void this.context?.close()
    this.context = null
  }

  private initialize(): void {
    const AudioConstructor = window.AudioContext
      ?? (window as Window & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext
    if (AudioConstructor === undefined) return
    const context = new AudioConstructor()
    const master = context.createGain()
    const musicGain = context.createGain()
    const sfxGain = context.createGain()
    const windGain = context.createGain()
    master.connect(context.destination)
    musicGain.connect(master)
    sfxGain.connect(master)
    windGain.connect(master)
    sfxGain.gain.value = 0.0001
    windGain.gain.value = 0

    for (const detune of [-8, 0, 7]) {
      const oscillator = context.createOscillator()
      oscillator.type = detune === 0 ? 'triangle' : 'sawtooth'
      oscillator.detune.value = detune
      oscillator.connect(musicGain)
      oscillator.start()
      this.musicOscillators.push(oscillator)
    }

    const effectOscillator = context.createOscillator()
    effectOscillator.type = 'square'
    effectOscillator.connect(sfxGain)
    effectOscillator.start()

    const noiseBuffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate)
    const channel = noiseBuffer.getChannelData(0)
    let noiseState = 0x51f15e
    for (let index = 0; index < channel.length; index += 1) {
      noiseState = Math.imul(noiseState ^ (noiseState >>> 15), 1 | noiseState)
      noiseState ^= noiseState + Math.imul(noiseState ^ (noiseState >>> 7), 61 | noiseState)
      channel[index] = ((noiseState ^ (noiseState >>> 14)) >>> 0) / 2_147_483_648 - 1
    }
    const noise = context.createBufferSource()
    const filter = context.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = 860
    filter.Q.value = 0.7
    noise.buffer = noiseBuffer
    noise.loop = true
    noise.connect(filter)
    filter.connect(windGain)
    noise.start()

    this.context = context
    this.master = master
    this.musicGain = musicGain
    this.sfxGain = sfxGain
    this.windGain = windGain
    this.effectOscillator = effectOscillator
  }

  private startSequencer(): void {
    if (this.stepTimer !== null) return
    const notes = [55, 55, 82.41, 65.41, 55, 98, 73.42, 65.41]
    this.stepTimer = window.setInterval(() => {
      if (this.context === null || this.muted) return
      const root = notes[this.sequenceStep % notes.length]!
      const chord = [root, root * 1.5, root * 2.01]
      const now = this.context.currentTime
      for (let index = 0; index < this.musicOscillators.length; index += 1) {
        this.musicOscillators[index]?.frequency.setTargetAtTime(chord[index] ?? root, now, 0.025)
      }
      this.sequenceStep += 1
    }, 250)
  }

  private applyLevels(): void {
    if (this.context === null || this.master === null || this.musicGain === null) return
    const now = this.context.currentTime
    this.master.gain.setTargetAtTime(this.muted ? 0 : 0.72, now, 0.03)
    this.musicGain.gain.setTargetAtTime(this.musicVolume * 0.055, now, 0.05)
  }
}
